import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  FormControlLabel, Switch, Stack
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ComboServices } from "./combos/index.js";
import { useApi } from "../hooks/useApi.js";
import { ui } from "../store/uiStore.js";
import type { Service } from "@kappa-v2/contracts";

interface Props {
  open: boolean;
  personId: string;
  period: string;
  partnerType?: string;
  onClose: () => void;
  onSaved: () => void;
}

// Aggiunge una NUOVA competenza (credito) a una ricevuta esistente: si sceglie il
// servizio dalla combo (Corsi/extra) → nome/importo/descrizione si precompilano da lì
// (descrizione e importo restano modificabili). POST /credits → il backend ricalcola la scheda.
export function CreditCreateDialog({ open, personId, period, partnerType, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { call } = useApi();
  const [serviceId, setServiceId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState(0);
  const [isDeducible, setIsDeducible] = useState(false);
  const [serviceType, setServiceType] = useState("");
  const [emailNotification, setEmailNotification] = useState(false);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState<Record<string, Service>>({});

  useEffect(() => {
    if (!open) return;
    call<{ data?: Service[] } | Service[]>("/api/v1/services", { silent: true, noLoader: true })
      .then((r) => {
        const list = Array.isArray(r) ? r : (r.data ?? []);
        setServices(Object.fromEntries(list.map((s) => [String(s._id), s])));
      })
      .catch(() => setServices({}));
  }, [call, open]);

  // Reset all'apertura.
  useEffect(() => {
    if (open) {
      setServiceId(""); setName(""); setDescription(""); setAmount(0);
      setIsDeducible(false); setServiceType(""); setEmailNotification(false);
    }
  }, [open]);

  const onSelectService = (id: string) => {
    setServiceId(id);
    const s = services[id];
    if (s) {
      setName(s.name ?? "");
      setDescription(s.description ?? "");
      setAmount(s.price ?? 0);
      setIsDeducible(Boolean(s.isDeducible));
      setServiceType(s.serviceType ?? "");
      setEmailNotification(Boolean(s.emailNotification));
    }
  };

  const save = async () => {
    if (!personId || !period || !serviceId || !name.trim()) return;
    setSaving(true);
    try {
      await call("/api/v1/payments/credits", {
        method: "POST",
        body: {
          personId,
          serviceId,
          period,
          credit: Number(amount) || 0,
          name: name.trim(),
          description: description.trim(),
          emailNotification,
          isDeducible,
          fromPartnerService: false,
          partnerType: partnerType ?? "form.partnerType.other",
          serviceType
        }
      });
      ui.toast(t("common.saved"), "success");
      onSaved();
      onClose();
    } catch {
      ui.toast(t("common.error"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("credits.addTitle")}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <ComboServices label={t("credits.competenceLabel")} value={serviceId} onChange={onSelectService} />
          <TextField fullWidth size="small" label={t("common.description")} value={description} onChange={(e) => setDescription(e.target.value)} multiline minRows={2} />
          <TextField fullWidth size="small" type="number" label={`${t("common.amount")} (€)`} value={amount}
            onChange={(e) => setAmount(e.target.value === "" ? 0 : Number(e.target.value))} inputProps={{ step: "0.01" }} />
          <FormControlLabel control={<Switch checked={isDeducible} onChange={(e) => setIsDeducible(e.target.checked)} />} label={t("credits.deductible")} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
        <Button variant="contained" onClick={save} disabled={saving || !serviceId || !name.trim()}>{t("common.save")}</Button>
      </DialogActions>
    </Dialog>
  );
}
